import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrganizationState } from '@/state';
import { LocaleLink } from '@/utils/locale';
import { useGetAiAgentConfig, useKbBuildLogs } from '@/views/AiAgents/queries';
import { kbLogMessage } from '@/views/AiAgents/utils/kbLogMessage';

import {
  AllLogsLink, UpdateRow, UpdatesList, UpdateText, UpdateTime,
} from './StefanKbUpdates.styled';

/** Pokazujemy aktualizacje z ostatniego tygodnia, najwyżej dwie; reszta jest w pełnych logach. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_ITEMS = 2;

/**
 * Log automatycznych aktualizacji bazy wiedzy agenta AI w czacie „Zapytaj AI”.
 * Bazę przebudowuje backend po każdej zmianie danych w panelu; hotel nic nie klika,
 * a tu widzi, kiedy agent nauczył się zmian i czego dotyczyły.
 */
export const StefanKbUpdates: FC = () => {
  const { t, i18n } = useTranslation();
  const { organizationId } = useOrganizationState();
  const { data: agentConfig } = useGetAiAgentConfig(organizationId);
  const { data: logs = [] } = useKbBuildLogs(organizationId);

  const recent = useMemo(
    () => logs.filter((log) => Date.now() - log.createdAt < MAX_AGE_MS).slice(0, MAX_ITEMS),
    [logs],
  );

  if (!agentConfig || recent.length === 0) return null;

  const timeLabel = (ms: number): string => new Date(ms).toLocaleString(i18n.language, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });

  return (
    <UpdatesList aria-label={t('helpAiChat.kbUpdates.label', 'Aktualizacje wiedzy agenta')}>
      {recent.map((log) => (
        <UpdateRow key={log.id} $error={!log.ok}>
          {log.ok ? <CheckCircle2 aria-hidden="true" /> : <AlertCircle aria-hidden="true" />}
          <UpdateText>
            {kbLogMessage(t, log, agentConfig.name)}
            <UpdateTime>{timeLabel(log.createdAt)}</UpdateTime>
          </UpdateText>
        </UpdateRow>
      ))}
      <AllLogsLink as={LocaleLink} to="/ai-agents/logs">
        {t('helpAiChat.kbUpdates.allLogs', 'Pełne logi')}
      </AllLogsLink>
    </UpdatesList>
  );
};
